import React from 'react';
import { Type, Square, Image as ImageIcon, Circle, CheckSquare, Sparkles, MoreHorizontal, BarChart3, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * Component Library - Sidebar with draggable components
 */
const ComponentLibrary = ({ onAddComponent }) => {
  const components = [
    {
      type: 'text',
      name: 'Text',
      icon: Type,
      defaultProps: {
        content: 'Text',
        width: 200,
        height: 40,
        styles: {
          fontSize: '16px',
          fontFamily: 'Inter, sans-serif',
          color: '#1f2937',
          fontWeight: '400',
          textAlign: 'left',
        },
      },
    },
    {
      type: 'button',
      name: 'Button',
      icon: Square,
      defaultProps: {
        content: 'Button',
        width: 200,
        height: 48,
        styles: {
          fontSize: '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: '600',
          color: '#ffffff',
          backgroundColor: '#3b82f6',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
        },
        action: 'none',
      },
    },
    {
      type: 'shape',
      name: 'Rectangle',
      icon: Square,
      defaultProps: {
        width: 200,
        height: 100,
        shapeType: 'rectangle',
        styles: {
          backgroundColor: '#3b82f6',
          borderRadius: '8px',
          border: 'none',
          opacity: 1,
        },
      },
    },
    {
      type: 'image',
      name: 'Image',
      icon: ImageIcon,
      defaultProps: {
        width: 200,
        height: 200,
        styles: {},
        imageUrl: null,
      },
    },
    {
      type: 'multiple-choice',
      name: 'Multiple Choice',
      icon: CheckSquare,
      defaultProps: {
        width: 300,
        height: 120,
        options: ['Option 1', 'Option 2', 'Option 3'],
        selectedOption: null,
        styles: {
          fontSize: '16px',
          fontFamily: 'Inter, sans-serif',
          color: '#1f2937',
        },
      },
    },
    {
      type: 'icon',
      name: 'Icon',
      icon: Sparkles,
      defaultProps: {
        width: 48,
        height: 48,
        iconName: 'heart',
        iconSize: 24,
        styles: {
          color: '#3b82f6',
        },
      },
    },
    {
      type: 'dots-indicator',
      name: 'Dots Indicator',
      icon: MoreHorizontal,
      defaultProps: {
        width: 120,
        height: 24,
        totalScreens: 3,
        currentScreen: 0,
        styles: {
          gap: '8px',
          dotSize: '8px',
          activeColor: '#3b82f6',
          inactiveColor: '#d1d5db',
        },
      },
    },
    {
      type: 'progress-bar',
      name: 'Progress Bar',
      icon: BarChart3,
      defaultProps: {
        width: 200,
        height: 8,
        progress: 33,
        styles: {
          backgroundColor: '#f3f4f6',
          fillColor: '#3b82f6',
          borderRadius: '4px',
        },
      },
    },
    {
      type: 'input',
      name: 'Input Field',
      icon: Type,
      defaultProps: {
        width: 300,
        height: 48,
        placeholder: 'Enter text...',
        label: 'Label',
        variableName: 'input_1',
        styles: {
          fontSize: '16px',
          fontFamily: 'Inter, sans-serif',
          color: '#1f2937',
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
          borderWidth: '1px',
          borderRadius: '8px',
          padding: '12px',
        },
      },
    },
  ];

  const handleComponentClick = (component) => {
    const newElement = {
      id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: component.type,
      name: component.name,
      x: 50,
      y: 50,
      width: component.defaultProps.width,
      height: component.defaultProps.height,
      content: component.defaultProps.content || '',
      styles: { ...component.defaultProps.styles },
      action: component.defaultProps.action || 'none',
      imageUrl: component.defaultProps.imageUrl || null,
      options: component.defaultProps.options || [],
      selectedOption: component.defaultProps.selectedOption || null,
      iconName: component.defaultProps.iconName || 'star',
      iconSize: component.defaultProps.iconSize || 24,
      totalScreens: component.defaultProps.totalScreens || 3,
      currentScreen: component.defaultProps.currentScreen || 0,
      progress: component.defaultProps.progress || 0,
      placeholder: component.defaultProps.placeholder || '',
      label: component.defaultProps.label || '',
      variableName: component.defaultProps.variableName || '',
      shapeType: component.defaultProps.shapeType || 'rectangle',
      rotation: 0,
    };
    onAddComponent(newElement);
  };

  const handleDragStart = (e, component) => {
    // Store component data in drag event
    e.dataTransfer.setData('application/json', JSON.stringify(component));
    e.dataTransfer.effectAllowed = 'copy';
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    // Restore visual feedback
    e.currentTarget.style.opacity = '1';
  };

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        Components
      </h3>
      <div className="space-y-2">
        {components.map((component) => {
          const Icon = component.icon;
          return (
            <Card
              key={component.type}
              className="cursor-move transition-all hover:border-primary hover:shadow-sm"
              onClick={() => handleComponentClick(component)}
              draggable
              onDragStart={(e) => handleDragStart(e, component)}
              onDragEnd={handleDragEnd}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">{component.name}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Suggest Component Button */}
      <div className="mt-6 pt-4 border-t border-border">
        <Button
          variant="outline"
          className="w-full bg-primary/5 hover:bg-primary/10 border-primary/20 hover:border-primary/40 text-primary font-medium"
          onClick={() => window.open('https://x.com/messages/compose?recipient_id=agibjames', '_blank')}
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Suggest a Component
        </Button>
      </div>
    </div>
  );
};

export default ComponentLibrary;
